import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DossierSettlement, DossierSettlementEvent, DossierSettlementEventKind } from '../../api/dossier';

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/** "€ 71,97", or a dash when there is no amount. */
export const money = (amount?: number | null): string => (amount === null || amount === undefined ? '–' : euro.format(amount));

const two = (n: number) => String(n).padStart(2, '0');

/**
 * "19-10-2026 14:00", or "19-10-2026" for a moment that only has a day: Exact dates its bank lines and
 * matches by day, and a made-up 02:00 would suggest an order that nobody knows.
 */
export const stamp = (iso?: string | null, dayOnly?: boolean): string => {
    if (!iso) {
        return 'zonder datum';
    }

    const d = new Date(iso);
    const day = `${two(d.getDate())}-${two(d.getMonth() + 1)}-${d.getFullYear()}`;

    return dayOnly ? day : `${day} ${two(d.getHours())}:${two(d.getMinutes())}`;
};

/** "02:31", for the moment something was read. */
export const clock = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const d = new Date(iso);

    return `${two(d.getHours())}:${two(d.getMinutes())}`;
};

export interface KindInfo {
    icon: IconProp;
    /** What it does to the invoice, in one line: the question support asks about every kind. */
    meaning: string;
}

/**
 * What each kind of event does to the invoice in the system it happens in. Shown next to every event,
 * so "afgeletterd" or "creditnota" never has to be looked up.
 */
export const KIND: Record<DossierSettlementEventKind, KindInfo> = {
    Invoice: { icon: 'file-invoice', meaning: 'Chargebee maakt de factuur; het hele bedrag staat open.' },
    SalesEntry: { icon: 'file-invoice', meaning: 'De connector boekt de factuur in Exact; daar staat hij nu ook open.' },
    BankPayment: {
        icon: 'building-columns',
        meaning: 'Er komt geld binnen op de bank in Exact. De factuur staat pas niet meer open als de betaling ertegen is afgeletterd.',
    },
    BankRefund: { icon: 'building-columns', meaning: 'Er gaat geld terug via de bank in Exact.' },
    Payment: { icon: 'hand-holding-dollar', meaning: 'Chargebee boekt een betaling op de factuur; wat open staat wordt kleiner.' },
    CreditNote: { icon: 'receipt', meaning: 'Een creditnota in Chargebee: de factuur wordt kleiner of wat betaald is wordt teruggegeven.' },
    CreditNoteBooked: {
        icon: 'receipt',
        meaning: 'De connector boekt de creditnota in Exact. Pas na afletteren tegen de factuur staat er minder open.',
    },
    Matched: { icon: 'scale-balanced', meaning: 'Exact koppelt betaling en factuur aan elkaar (afletteren): niets staat meer open.' },
    PartlyMatched: {
        icon: 'scale-balanced',
        meaning: 'Exact lettert een deel af en splitst de factuur in een afgeletterd deel en een deel dat nog open staat.',
    },
    Chargeback: {
        icon: 'rotate-left',
        meaning: 'De bank van de klant haalt de betaling terug (stornering): in Chargebee staat de factuur weer open.',
    },
    BankRemoved: {
        icon: 'ban',
        meaning: 'De connector haalt de bankboeking van de gestorneerde betaling weg; de factuur staat in Exact weer open.',
    },
    Other: { icon: 'circle-info', meaning: '' },
};

export const kindOf = (event: DossierSettlementEvent): DossierSettlementEventKind => event.kind ?? 'Other';

/** Exact answered and has no sales entry for the invoice: it never got there, rather than not being asked. */
export const missingInExact = (settlement: DossierSettlement): boolean =>
    !!settlement.exactRead && !settlement.events.some((e) => e.kind === 'SalesEntry');

/** Two amounts the same to the cent. */
export const same = (a?: number | null, b?: number | null): boolean =>
    a !== null && a !== undefined && b !== null && b !== undefined && Math.abs(a - b) < 0.005;
